import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { addDoc, collection, getDocs, doc, getDoc, runTransaction, updateDoc } from 'firebase/firestore';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './Cart.css';
import './QuantityStepper.css';
import './Receipt.css';
import Notifications from './Notification';
import 'jspdf-autotable';
import mercadopagoLogo from './../Logos/mercadopago.png';
import { useTracking } from '../hooks/useTracking';
import useGlobalTheme from '../hooks/useGlobalTheme';
import { computeCartDiscounts, toMercadoPagoLines } from '../utils/cartDiscounts';

// URLs de las Cloud Functions (constante fuera del componente)
const FUNCTIONS_URLS = {
  createPreference: process.env.REACT_APP_CREATE_PREFERENCE_URL,
  verifyReCaptcha: process.env.REACT_APP_VERIFY_RECAPTCHA_URL,
  handlePaymentNotification: process.env.REACT_APP_HANDLE_PAYMENT_NOTIFICATION_URL
};

const Cart = ({ cart, setCart, removeFromCart, clearCart, updateCartItemQuantity }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [formData, setFormData] = useState({
    nombre: '', telefono: '', email: '', domicilio: '', localidad: '',
    codigoPostal: '', provincia: '', pais: '', informacionAdicional: '', medioDePago: '',
    cuit: '' // Opcional: para recibir factura con su CUIT/CUIL (Factura C)
  });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingDiscounts, setShippingDiscounts] = useState([]);
  const [deliveryType, setDeliveryType] = useState('shipping'); // 'shipping' | 'pickup'
  const { trackBeginCheckout } = useTracking();
  const { isDarkMode } = useGlobalTheme();
  // Umbral AFIP: por encima de este monto es obligatorio CUIT/DNI para Factura C (configurable en Firestore config/facturacion)
  const [montoMaxSinCuit, setMontoMaxSinCuit] = useState(417288);
  // Stock disponible por producto (productId -> stock) para limitar cantidad al máximo disponible
  const [productStocks, setProductStocks] = useState({});

  useEffect(() => {
    const fetchStocks = async () => {
      if (!cart.length) {
        setProductStocks({});
        return;
      }
      const stocks = {};
      await Promise.all(
        cart.map(async (item) => {
          try {
            const productRef = doc(db, 'products', item.id);
            const productDoc = await getDoc(productRef);
            if (!productDoc.exists()) {
              stocks[item.id] = 0;
              return;
            }
            const data = productDoc.data();
            stocks[item.id] = data.excludeFromStock === true ? 999 : (Number(data.stock) || 0);
          } catch (err) {
            stocks[item.id] = 0;
          }
        })
      );
      setProductStocks(stocks);
    };
    fetchStocks();
  }, [cart]);

  useEffect(() => {
    const fetchFacturacionConfig = async () => {
      try {
        const configRef = doc(db, 'config', 'facturacion');
        const configSnap = await getDoc(configRef);
        if (configSnap.exists() && configSnap.data().montoMaxSinCuit != null) {
          setMontoMaxSinCuit(Number(configSnap.data().montoMaxSinCuit));
        }
      } catch (err) {
        console.warn('No se pudo cargar config facturacion, usando umbral por defecto:', err);
      }
    };
    fetchFacturacionConfig();
  }, []);

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'discounts'));
        const fetchedDiscounts = querySnapshot.docs.map(doc => doc.data());
        setDiscounts(fetchedDiscounts);
      } catch (error) {
        console.error("Error fetching discounts:", error);
      }
    };
    fetchDiscounts();
  }, []);

  const formatNumber = (price) => {
    return price
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const calculateShippingCostValue = useCallback((cartItems, shippingCosts, shippingDiscounts) => {
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    
    // Calcular costo de envío básico
    const applicableShippingCost = shippingCosts
      .filter(cost => totalQuantity >= cost.minQuantity)
      .sort((a, b) => b.minQuantity - a.minQuantity)[0];
    
    let baseShippingCost = applicableShippingCost ? applicableShippingCost.shippingCost : 0;
    
    // Aplicar descuentos de envío por monto
    const applicableShippingDiscount = shippingDiscounts
      .filter(discount => subtotal >= discount.minAmount)
      .sort((a, b) => b.minAmount - a.minAmount)[0];
    
    let finalShippingCost = baseShippingCost;
    
    if (applicableShippingDiscount) {
      // Si el descuento es 0, envío gratis
      if (applicableShippingDiscount.shippingDiscount === 0) {
        finalShippingCost = 0;
      } else {
        // Aplicar descuento al costo de envío
        finalShippingCost = Math.max(0, baseShippingCost - applicableShippingDiscount.shippingDiscount);
      }
    }
    
    return finalShippingCost;
  }, []);

  const calculateShippingCost = useCallback((cartItems, shippingCosts, shippingDiscounts) => {
    setShippingCost(calculateShippingCostValue(cartItems, shippingCosts, shippingDiscounts));
  }, [calculateShippingCostValue]);

  const fetchCurrentShippingCost = useCallback(async (cartItems) => {
    if (deliveryType === 'pickup') {
      setShippingCost(0);
      return 0;
    }

    const shippingCostsSnapshot = await getDocs(collection(db, 'shippingCosts'));
    const fetchedShippingCosts = shippingCostsSnapshot.docs.map(doc => doc.data());
    const shippingDiscountsSnapshot = await getDocs(collection(db, 'shippingDiscounts'));
    const fetchedShippingDiscounts = shippingDiscountsSnapshot.docs.map(doc => doc.data());
    setShippingDiscounts(fetchedShippingDiscounts);

    const currentShippingCost = calculateShippingCostValue(cartItems, fetchedShippingCosts, fetchedShippingDiscounts);
    setShippingCost(currentShippingCost);
    return currentShippingCost;
  }, [calculateShippingCostValue, deliveryType]);

  useEffect(() => {
    const fetchShippingData = async () => {
      try {
        if (deliveryType === 'pickup') {
          setShippingCost(0);
          return;
        }
        // Cargar costos de envío básicos
        const shippingCostsSnapshot = await getDocs(collection(db, 'shippingCosts'));
        const fetchedShippingCosts = shippingCostsSnapshot.docs.map(doc => doc.data());
        
        // Cargar descuentos de envío
        const shippingDiscountsSnapshot = await getDocs(collection(db, 'shippingDiscounts'));
        const fetchedShippingDiscounts = shippingDiscountsSnapshot.docs.map(doc => doc.data());
        
        setShippingDiscounts(fetchedShippingDiscounts);
        calculateShippingCost(cart, fetchedShippingCosts, fetchedShippingDiscounts);
      } catch (error) {
        console.error("Error fetching shipping data:", error);
      }
    };

    fetchShippingData();
  }, [cart, deliveryType, calculateShippingCost]);

  const getNextShippingDiscount = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const nextDiscount = shippingDiscounts
      .filter(discount => subtotal < discount.minAmount)
      .sort((a, b) => a.minAmount - b.minAmount)[0];
    
    return nextDiscount;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showNotification = (message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 5000);
  };

  // CUIT/DNI válido: al menos 8 dígitos (DNI) o 11 (CUIT). AFIP exige identificación por encima de cierto monto.
  const cuitDigits = (v) => String(v || '').replace(/\D/g, '');
  const hasValidCuitOrDni = (v) => cuitDigits(v).length >= 8;
  const requiresCuitByAfip = (total) => total >= montoMaxSinCuit;

  const refreshCartWithCurrentProducts = async ({ notifyOnChange = false } = {}) => {
    if (!cart.length) return [];

    const refreshedItems = [];
    let changed = false;
    let removedItems = false;

    for (const item of cart) {
      const productRef = doc(db, 'products', item.id);
      const productDoc = await getDoc(productRef);
      if (!productDoc.exists()) {
        changed = true;
        removedItems = true;
        continue;
      }

      const productData = productDoc.data();
      const excludeFromStock = productData.excludeFromStock === true;
      const currentStock = excludeFromStock ? 999 : Number(productData.stock) || 0;

      if (!excludeFromStock && currentStock <= 0) {
        changed = true;
        removedItems = true;
        continue;
      }

      const quantity = excludeFromStock ? item.quantity : Math.min(item.quantity, currentStock);
      const refreshedItem = { ...item, ...productData, id: item.id, quantity };
      if (
        quantity !== item.quantity ||
        Number(refreshedItem.price) !== Number(item.price) ||
        refreshedItem.model !== item.model ||
        refreshedItem.discountExcluded !== item.discountExcluded
      ) {
        changed = true;
      }
      refreshedItems.push(refreshedItem);
    }

    if (changed) {
      setCart(refreshedItems);
      if (notifyOnChange) {
        showNotification(
          removedItems
            ? 'Actualizamos tu carrito: quitamos productos sin disponibilidad y refrescamos precios vigentes.'
            : 'Actualizamos tu carrito con precios y stock vigentes. Revisá el total antes de finalizar.'
        );
      }
    }

    return refreshedItems;
  };

  useEffect(() => {
    const syncCartPrices = async () => {
      try {
        await refreshCartWithCurrentProducts({ notifyOnChange: true });
      } catch (error) {
        console.error('Error refreshing cart prices:', error);
        showNotification('No pudimos actualizar precios del carrito. Verificá tu conexión e intentá nuevamente.');
      }
    };

    syncCartPrices();
    // Solo sincroniza cuando cambia el contenido general del carrito para evitar ciclos al actualizar precios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]);

  const checkStockAvailability = async (cartItems = cart) => {
    for (const item of cartItems) {
      const productRef = doc(db, 'products', item.id);
      const productDoc = await getDoc(productRef);
      const productData = productDoc.exists() ? productDoc.data() : null;
      if (!productData) return false;
      if (productData.excludeFromStock === true) continue;
      if (productData.stock < item.quantity) return false;
    }
    return true;
  };

  const getOrderNumber = async (type) => {
    const counterRef = doc(db, 'counters', type === 'sale' ? 'saleCounter' : 'orderCounter');
    try {
      const newOrderNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        if (!counterDoc.exists()) {
          throw new Error(`${type === 'sale' ? 'Sale' : 'Order'} counter document does not exist!`);
        }
        const newOrderNumber = counterDoc.data().currentOrderNumber + 1;
        transaction.update(counterRef, { currentOrderNumber: newOrderNumber });
        return newOrderNumber;
      });
      return newOrderNumber;
    } catch (error) {
      console.error("Transaction failed: ", error);
      showNotification(`Error generating ${type === 'sale' ? 'sale' : 'order'} number, please try again.`);
      return null;
    }
  };

  const verifyReCaptchaToken = async (token) => {
    if (!FUNCTIONS_URLS.verifyReCaptcha || !token) return false;
    try {
      const response = await fetch(FUNCTIONS_URLS.verifyReCaptcha, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const result = await response.json();
        return !!result.success;
      }
    } catch (error) {
      console.warn('reCAPTCHA server not available:', error);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validaciones básicas
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        showNotification('Dirección de correo electrónico no válida.');
        setIsLoading(false);
        return;
      }

      const isPickup = deliveryType === 'pickup';
      if (!formData.nombre.trim() || !formData.telefono.trim() || !formData.email.trim()) {
        showNotification('Por favor, complete nombre, teléfono y email.');
        setIsLoading(false);
        return;
      }
      if (!isPickup && !formData.domicilio.trim()) {
        showNotification('Por favor, complete el domicilio de envío.');
        setIsLoading(false);
        return;
      }

      const currentCart = await refreshCartWithCurrentProducts({ notifyOnChange: true });
      if (!currentCart.length) {
        showNotification('El carrito ya no tiene productos disponibles.');
        setIsLoading(false);
        return;
      }

      const currentShippingCost = await fetchCurrentShippingCost(currentCart);

      const isStockAvailable = await checkStockAvailability(currentCart);
      if (!isStockAvailable) {
        showNotification('No hay inventario disponible, elimine items del carrito agotados.');
        setIsLoading(false);
        return;
      }

      const { subtotal, discountAmount, discountDetail, finalPrice } = calculateTotalPrice(currentCart, currentShippingCost);
      if (requiresCuitByAfip(finalPrice) && !hasValidCuitOrDni(formData.cuit)) {
        showNotification(`Para compras de $${formatNumber(montoMaxSinCuit)} o más es obligatorio cargar CUIT o DNI para la factura (AFIP).`);
        setIsLoading(false);
        return;
      }

      // Generar y verificar token reCAPTCHA (no bloqueante: si falla, el pedido se envía igual)
      let recaptchaValid = false;
      if (executeRecaptcha) {
        try {
          const recaptchaToken = await executeRecaptcha('submit_order');
          recaptchaValid = await verifyReCaptchaToken(recaptchaToken);
        } catch (error) {
          console.warn('reCAPTCHA no disponible, enviando pedido sin verificación:', error);
        }
      }

      // Obtener número de orden
      const orderNumber = await getOrderNumber('order');
      if (!orderNumber) {
        setIsLoading(false);
        return;
      }

      const orderFormData = isPickup
        ? { ...formData, domicilio: 'Retiro en local', localidad: formData.localidad || 'Retiro en local', codigoPostal: '-', provincia: formData.provincia || '-', pais: formData.pais || 'Argentina' }
        : formData;

      const cuitVal = (formData.cuit || '').trim() || null;
      const orderData = {
        orderNumber,
        ...orderFormData,
        medioDePago: formData.medioDePago?.trim() || 'Por definir',
        deliveryType: deliveryType,
        pickupAtStore: isPickup,
        cartItems: currentCart,
        subtotal,
        discountAmount,
        discountDetail,
        shippingCost: currentShippingCost,
        totalPrice: finalPrice,
        date: new Date(),
        status: 'Pendiente',
        recaptchaVerified: recaptchaValid,
        userAgent: navigator.userAgent,
        emailsSent: false,  // Se actualizará cuando las Cloud Functions envíen los emails
        cuit: cuitVal  // Opcional: para factura con CUIT/CUIL del comprador
      };

      // Guardar en Firestore - esto disparará automáticamente los emails
      const docRef = await addDoc(collection(db, 'pedidos'), { ...orderData, isActive: true });
      console.log('Order saved with ID:', docRef.id);

      // Limpiar formulario antes de navegar
      setShowForm(false);
      setFormData({
        nombre: '', telefono: '', email: '', domicilio: '', localidad: '',
        codigoPostal: '', provincia: '', pais: '', informacionAdicional: '', medioDePago: '', cuit: '',
      });
      clearCart();
      setKeyword('');
      setAppliedDiscount(null);
      navigate('/pedido-enviado', { state: { orderNumber }, replace: true });

    } catch (error) {
      console.error("Error al enviar el pedido:", error);
      
      if (error.code === 'permission-denied') {
        showNotification("Error de permisos. Por favor, contacte al administrador.");
      } else if (error.message.includes('network')) {
        showNotification("Error de conexión. Verifique su internet e intente nuevamente.");
      } else {
        showNotification("Error al procesar el pedido. Por favor, intente nuevamente o contacte por WhatsApp.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createMercadoPagoPreference = async (orderData, items) => {
    try {
      const requestData = {
        items,
        external_reference: orderData.docId
      };

      const response = await fetch(FUNCTIONS_URLS.createPreference, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        return data.init_point;
      } else {
        console.error('MercadoPago preference creation failed:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error calling createPreference function:', error);
      return null;
    }
  };

  const handleCheckoutPro = async () => {
    setIsLoading(true);
    trackBeginCheckout({
      total: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
      items: cart.map(item => ({
        id: item.id,
        name: item.model,
        price: item.price,
        quantity: item.quantity
      }))
    });

    try {
      // Validaciones básicas
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        showNotification('Dirección de correo electrónico no válida.');
        setIsLoading(false);
        return;
      }

      const isPickupCheckout = deliveryType === 'pickup';
      if (!formData.nombre || !formData.email || !formData.telefono) {
        showNotification('Por favor, complete nombre, email y teléfono.');
        setIsLoading(false);
        return;
      }
      if (!isPickupCheckout && !formData.domicilio) {
        showNotification('Por favor, complete la dirección de envío antes de proceder al pago.');
        setIsLoading(false);
        return;
      }

      const currentCart = await refreshCartWithCurrentProducts({ notifyOnChange: true });
      if (!currentCart.length) {
        showNotification('El carrito ya no tiene productos disponibles.');
        setIsLoading(false);
        return;
      }

      const currentShippingCost = await fetchCurrentShippingCost(currentCart);

      const isStockAvailable = await checkStockAvailability(currentCart);
      if (!isStockAvailable) {
        showNotification('No hay inventario disponible, elimine items del carrito agotados.');
        setIsLoading(false);
        return;
      }

      const { subtotal, discountAmount, discountDetail, linesById, finalPrice } = calculateTotalPrice(currentCart, currentShippingCost);
      if (requiresCuitByAfip(finalPrice) && !hasValidCuitOrDni(formData.cuit)) {
        showNotification(`Para compras de $${formatNumber(montoMaxSinCuit)} o más es obligatorio cargar CUIT o DNI para la factura (AFIP).`);
        setIsLoading(false);
        return;
      }

      // Verificar reCAPTCHA (no bloqueante: si falla, la compra continúa)
      let recaptchaValid = false;
      if (executeRecaptcha) {
        try {
          const recaptchaToken = await executeRecaptcha('checkout_mercadopago');
          recaptchaValid = await verifyReCaptchaToken(recaptchaToken);
        } catch (error) {
          console.warn('reCAPTCHA no disponible, continuando checkout sin verificación:', error);
        }
      }

      const items = currentCart.flatMap((item) => {
        const line = linesById[item.id];
        const total = line ? line.discountedTotal : item.price * item.quantity;
        return toMercadoPagoLines({
          title: item.model,
          id: item.id,
          quantity: item.quantity,
          total
        });
      });

      // Agregar costo de envío como ítem separado solo si no es retiro en local
      if (!isPickupCheckout && currentShippingCost > 0) {
        items.push({
          title: 'Costo de Envío',
          unit_price: Math.round((Number(currentShippingCost) + Number.EPSILON) * 100) / 100,
          quantity: 1,
          id: 'shipping_cost'
        });
      }

      const orderNumber = await getOrderNumber('sale');
      if (!orderNumber) {
        setIsLoading(false);
        return;
      }

      const checkoutFormData = isPickupCheckout
        ? { ...formData, domicilio: 'Retiro en local', localidad: formData.localidad || 'Retiro en local', codigoPostal: '-', provincia: formData.provincia || '-', pais: formData.pais || 'Argentina' }
        : formData;

      const cuitVal = (formData.cuit || '').trim() || null;
      const orderData = {
        orderNumber,
        ...checkoutFormData,
        deliveryType: deliveryType,
        pickupAtStore: isPickupCheckout,
        cartItems: currentCart,
        subtotal,
        discountAmount,
        discountDetail,
        shippingCost: isPickupCheckout ? 0 : currentShippingCost,
        totalPrice: finalPrice,
        date: new Date(),
        status: 'Procesando',
        isPaid: false,
        paymentMethod: 'MercadoPago',
        userAgent: navigator.userAgent,
        recaptchaVerified: recaptchaValid,
        emailsSent: false,  // Se actualiza cuando el pago está acreditado y se envían los emails (webhook)
        cuit: cuitVal  // Opcional: para factura con CUIT/CUIL del comprador
      };

      console.log('Creating order document in Firestore...');
      // Guardar en Firestore; los emails de confirmación se envían cuando MercadoPago acredite el pago (webhook)
      const orderRef = await addDoc(collection(db, 'compras_mercadopago'), { 
        ...orderData, 
        isActive: true 
      });
      console.log('Order document created with ID:', orderRef.id);

      // Intentar crear preferencia de MercadoPago
      console.log('Attempting to create MercadoPago preference...');
      const initPoint = await createMercadoPagoPreference({ 
        ...orderData, 
        docId: orderRef.id 
      }, items);

      if (initPoint) {
        console.log('MercadoPago preference created successfully, preparing redirect...');

        // Limpiar carrito y formulario
        clearCart();
        setKeyword('');
        setAppliedDiscount(null);
        setShowCheckoutForm(false);
        
        showNotification('Redirigiendo a MercadoPago...');
        
        // Redirigir a MercadoPago
        setTimeout(() => {
          window.location.href = initPoint;
        }, 1500);

      } else {
        // Fallback: MercadoPago no disponible
        console.warn('MercadoPago not available, saving order for manual processing');
        
        try {
          await updateDoc(orderRef, {
            status: 'Pendiente - Pago Manual',
            notes: 'MercadoPago no disponible en el momento de la compra',
            paymentMethod: 'Manual - Por definir',
            lastUpdated: new Date()
          });

          showNotification(`Orden #${orderNumber} registrada. MercadoPago no está disponible temporalmente. Te contactaremos para coordinar el pago.`);
        } catch (updateError) {
          console.error('Error updating order status:', updateError);
          showNotification(`Orden #${orderNumber} registrada exitosamente. Te contactaremos pronto.`);
        }
        
        // Limpiar formulario
        setShowCheckoutForm(false);
        clearCart();
        setKeyword('');
        setAppliedDiscount(null);
      }

    } catch (error) {
      console.error('Error al procesar la compra:', error);
      
      let errorMessage = 'Error al procesar la compra. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Error de permisos en Firestore. Por favor, contacte al administrador.';
      } else if (error.message.includes('network') || error.name === 'TypeError') {
        errorMessage += 'Error de conexión. Verifique su internet e intente nuevamente.';
      } else if (error.message.includes('updateDoc') || error.message.includes('update')) {
        errorMessage += 'Orden registrada exitosamente. Error al actualizar estado, pero su pedido fue recibido.';
      } else {
        errorMessage += 'Su orden pudo haber sido registrada. Por favor contacte por WhatsApp para confirmar.';
      }
      
      showNotification(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalPrice = (cartItems = cart, currentShippingCost = shippingCost) => {
    const result = computeCartDiscounts(cartItems, discounts, { appliedDiscount });
    const shippingCents = Math.round(((Number(currentShippingCost) || 0) + Number.EPSILON) * 100);
    const finalPrice = (result.subtotalCents - result.discountCents + shippingCents) / 100;

    return { ...result, shippingCost: Number(currentShippingCost) || 0, finalPrice };
  };

  const handleApplyDiscount = () => {
    const foundDiscount = discounts.find(discount => discount.type === 'keyword' && discount.keyword === keyword);
    if (foundDiscount) {
      setAppliedDiscount(foundDiscount);
      showNotification(`Descuento del ${foundDiscount.percentage}% aplicado!`);
    } else {
      showNotification('Código de descuento no válido.');
    }
  };

  const toggleForm = (formType) => {
    if (formType === 'order') {
      setShowForm(!showForm);
      setShowCheckoutForm(false);
    } else if (formType === 'checkout') {
      setShowCheckoutForm(!showCheckoutForm);
      setShowForm(false);
    }
  };

  const MIN_QUANTITY = 1;
  const MAX_QUANTITY = 999; // tope máximo cuando el stock aún no se cargó; getMaxQuantity usa el stock real

  const getMaxQuantity = (itemId) => {
    const stock = productStocks[itemId];
    return typeof stock === 'number' ? Math.max(0, stock) : MAX_QUANTITY;
  };

  const handleQuantityChange = (e, itemId) => {
    const raw = e.target.value;
    if (raw === '') return;
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) return;
    const maxQ = getMaxQuantity(itemId);
    const clamped = Math.min(maxQ, Math.max(MIN_QUANTITY, num));
    updateCartItemQuantity(itemId, clamped);
  };

  const handleQuantityBlur = (e, itemId) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      updateCartItemQuantity(itemId, MIN_QUANTITY);
      return;
    }
    const num = parseInt(raw, 10);
    const maxQ = getMaxQuantity(itemId);
    if (Number.isNaN(num) || num < MIN_QUANTITY) {
      updateCartItemQuantity(itemId, MIN_QUANTITY);
    } else if (num > maxQ) {
      updateCartItemQuantity(itemId, maxQ);
    }
  };

  const handleQuantityStep = (itemId, currentQuantity, delta) => {
    const maxQ = getMaxQuantity(itemId);
    const next = currentQuantity + delta;
    if (next >= MIN_QUANTITY && next <= maxQ) {
      updateCartItemQuantity(itemId, next);
    }
  };

  const { subtotal, discountAmount, discountDetail, linesById, finalPrice } = calculateTotalPrice();
  const nextShippingDiscount =
    cart.length > 0 && deliveryType === 'shipping' ? getNextShippingDiscount() : null;

  return (
    <div className={`cart ${isDarkMode ? 'dark' : 'light'}`}>
      <Notifications message={notification.message} show={notification.show} />
      {isLoading && (
        <div className="cart-loading-overlay" role="status" aria-live="polite" aria-label="Procesando">
          <div className="cart-loading-card">
            <div className="cart-loading-spinner" aria-hidden />
            <p className="cart-loading-label">
              {showCheckoutForm ? 'Procesando tu compra' : 'Enviando tu pedido'}
            </p>
            <p className="cart-loading-hint">
              {showCheckoutForm ? 'Serás redirigido a MercadoPago en un momento.' : 'Guardando datos y preparando la confirmación…'}
            </p>
          </div>
        </div>
      )}
      
      <div className="cart-header">
        <h1 className="cart-page-title">
          <span className="cart-page-title-text">Carrito de Compras</span>
        </h1>
      </div>
      {cart.length === 0 ? (
        <p className="cart-empty-message">El carrito está vacío.</p>
      ) : (
        <div>
          {cart.map(item => {
            const line = linesById[item.id];
            const itemSubtotal = line ? line.originalTotal : item.price * item.quantity;
            const totalWithDiscount = line ? line.discountedTotal : itemSubtotal;
            const hasDiscount = !!(line && line.savings > 0);
            const unitPriceWithDiscount = item.quantity > 0 ? totalWithDiscount / item.quantity : item.price;
            const linePercentage = line ? line.bestDiscount.percentage : 0;
            const maxQuantity = getMaxQuantity(item.id);

            return (
              <div key={item.id} className="cart-item">
                <div className="cart-item-top">
                  <div className="cart-item-image-wrap">
                    <img src={item.images ? item.images[0] : ''} alt={item.model} className="cart-item-image" />
                  </div>
                  <div className="cart-item-main">
                    <div className="cart-item-details">
                      <h4 className="cart-item-name">{item.model}</h4>
                      {item.discountExcluded && (
                        <p className="cart-item-no-discount-notice">Este producto no aplica para descuento</p>
                      )}
                      {hasDiscount && (
                        <p className="cart-item-has-discount-notice">Este producto tiene un descuento del {linePercentage}%</p>
                      )}
                    </div>
                    <div className="cart-item-qty" role="group" aria-label="Cantidad">
                      <span className="cart-item-metric-label">Cantidad</span>
                      <div className="quantity-stepper">
                        <button
                          type="button"
                          className="quantity-stepper-btn quantity-stepper-btn--minus"
                          onClick={() => handleQuantityStep(item.id, item.quantity, -1)}
                          disabled={item.quantity <= MIN_QUANTITY}
                          aria-label="Reducir cantidad"
                        >
                          −
                        </button>
                        <div className="quantity-stepper-field">
                          <input
                            type="number"
                            value={item.quantity}
                            min={MIN_QUANTITY}
                            max={maxQuantity}
                            onChange={(e) => handleQuantityChange(e, item.id)}
                            onBlur={(e) => handleQuantityBlur(e, item.id)}
                            className="quantity-input"
                            aria-label="Cantidad"
                            inputMode="numeric"
                            pattern="[0-9]*"
                          />
                        </div>
                        <button
                          type="button"
                          className="quantity-stepper-btn quantity-stepper-btn--plus"
                          onClick={() => handleQuantityStep(item.id, item.quantity, 1)}
                          disabled={item.quantity >= maxQuantity}
                          aria-label="Aumentar cantidad"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="cart-item-actions">
                    <button type="button" className="cart-item-remove-btn" onClick={() => removeFromCart(item.id)} aria-label="Eliminar del carrito">
                      <svg
                        className="cart-item-remove-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="cart-item-remove-label">Eliminar</span>
                    </button>
                  </div>
                </div>
                <div className="cart-item-metrics" role="group" aria-label="Precios del producto">
                  <div className="cart-item-metric">
                    <span className="cart-item-metric-label">Precio unit.</span>
                    <div className="cart-item-metric-value cart-item-price-wrap">
                      {hasDiscount ? (
                        <>
                          <span className="cart-item-price-original" aria-hidden="true">${formatNumber(item.price)}</span>
                          <span className="cart-item-price-discounted">${formatNumber(unitPriceWithDiscount)}</span>
                        </>
                      ) : (
                        <span className="cart-item-value">${formatNumber(item.price)}</span>
                      )}
                    </div>
                  </div>
                  <div className="cart-item-metric cart-item-metric--total">
                    <span className="cart-item-metric-label">Total producto</span>
                    <div className="cart-item-metric-value cart-item-total-wrap">
                      {hasDiscount ? (
                        <>
                          <span className="cart-item-total-original" aria-hidden="true">${formatNumber(itemSubtotal)}</span>
                          <span className="cart-item-total-discounted">${formatNumber(totalWithDiscount)}</span>
                        </>
                      ) : (
                        <span className="cart-item-total-value">${formatNumber(itemSubtotal)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          <div className="cart-total">
            <div className="cart-delivery-type">
              <span className="cart-delivery-type-label">¿Cómo querés recibir tu pedido?</span>
              <div className="cart-delivery-type-options">
                <label className={`cart-delivery-option ${deliveryType === 'shipping' ? 'cart-delivery-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryType"
                    value="shipping"
                    checked={deliveryType === 'shipping'}
                    onChange={() => setDeliveryType('shipping')}
                  />
                  <span className="cart-delivery-option-text">Envío a domicilio</span>
                </label>
                <label className={`cart-delivery-option ${deliveryType === 'pickup' ? 'cart-delivery-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="deliveryType"
                    value="pickup"
                    checked={deliveryType === 'pickup'}
                    onChange={() => setDeliveryType('pickup')}
                  />
                  <span className="cart-delivery-option-text">Retiro en local (sin costo de envío)</span>
                </label>
              </div>
            </div>
            <div className="cart-summary-line cart-summary-line--subtotal" role="group" aria-label="Subtotal">
              <span className="cart-summary-label">Subtotal</span>
              <span className="cart-summary-amount">${formatNumber(subtotal)}</span>
            </div>
            {discountDetail && (
              <div className="cart-summary-line cart-summary-line--discount" role="group" aria-label="Descuento">
                <span className="cart-summary-label">{discountDetail}</span>
                <span className="cart-summary-amount cart-summary-amount--savings">-${formatNumber(discountAmount)}</span>
              </div>
            )}
            <div className="cart-summary-line cart-summary-line--shipping" role="group" aria-label="Envío o retiro">
              <span className="cart-summary-label">
                {deliveryType === 'pickup' ? 'Retiro en local' : 'Costo de envío'}
              </span>
              <span className="cart-summary-amount cart-summary-amount--shipping">
                {deliveryType === 'pickup' ? '$0' : `$${formatNumber(shippingCost)}`}
              </span>
            </div>
            {nextShippingDiscount && (
              <div className="cart-shipping-hint" role="note">
                <span className="cart-shipping-hint-lead">Tip de envío:</span>{' '}
                Agrega ${formatNumber(nextShippingDiscount.minAmount - subtotal)} más para obtener
                {nextShippingDiscount.shippingDiscount === 0 ? ' envío gratis' : ` $${formatNumber(nextShippingDiscount.shippingDiscount)} de descuento en envío`}
              </div>
            )}
            <div className="cart-summary-line cart-summary-line--grand-total" role="group" aria-label="Total del pedido">
              <span className="cart-summary-label">Total{deliveryType === 'pickup' ? '' : ' con envío'}</span>
              <span className="cart-summary-amount cart-summary-amount--grand">${formatNumber(finalPrice)}</span>
            </div>
            
            <div className="discount-code">
              <input
                type="text"
                placeholder="Código de descuento"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                    e.preventDefault();
                    handleApplyDiscount();
                  }
                }}
                className="discount-input"
                maxLength={50}
                autoComplete="off"
              />
              {keyword.trim() ? (
                <button type="button" onClick={handleApplyDiscount} className="view-toggle-btn discount-code-apply-btn">
                  Aplicar descuento
                </button>
              ) : null}
            </div>
            
            <div className="cart-buttons">
              <button className="cart-button cart-button--order" onClick={() => toggleForm('order')}>
                <span className="cart-button-label">{showForm ? 'Ocultar formulario' : 'Enviar pedido'}</span>
                <span className="cart-button-tooltip">Para otro medio de pago distinto a MercadoPago</span>
              </button>
              <button className="cart-button cart-button--mp" onClick={() => toggleForm('checkout')}>
                <span className="cart-button-label">
                  {showCheckoutForm ? 'Ocultar formulario' : 'Comprar con MercadoPago'}
                  <img src={mercadopagoLogo} alt="MercadoPago" className="mercadopago-logo" />
                </span>
                <span className="cart-button-tooltip">Para pagar con tarjeta o dinero de MercadoPago</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="order-form">
          <h3 className="order-form-title">Información para solicitud de pedido</h3>
          {deliveryType === 'pickup' && (
            <p className="cart-pickup-notice">Retiro en local: no es necesario completar la dirección de envío.</p>
          )}
          <form onSubmit={handleSubmit}>
            <input type="text" name="nombre" placeholder="Nombre completo y Apellido *" value={formData.nombre} onChange={handleChange} maxLength={50} required />
            <input type="tel" name="telefono" placeholder="Teléfono *" value={formData.telefono} onChange={handleChange} maxLength={20} required />
            <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleChange} maxLength={100} required />
            <input type="text" name="domicilio" placeholder={deliveryType === 'pickup' ? 'Domicilio (opcional - retiro en local)' : 'Domicilio *'} value={formData.domicilio} onChange={handleChange} maxLength={100} required={deliveryType !== 'pickup'} />
            <input type="text" name="localidad" placeholder={deliveryType === 'pickup' ? 'Localidad (opcional)' : 'Localidad *'} value={formData.localidad} onChange={handleChange} maxLength={50} required={deliveryType !== 'pickup'} />
            <input type="text" name="codigoPostal" placeholder={deliveryType === 'pickup' ? 'Código Postal (opcional)' : 'Código Postal *'} value={formData.codigoPostal} onChange={handleChange} maxLength={10} required={deliveryType !== 'pickup'} />
            <input type="text" name="provincia" placeholder={deliveryType === 'pickup' ? 'Provincia (opcional)' : 'Provincia *'} value={formData.provincia} onChange={handleChange} maxLength={50} required={deliveryType !== 'pickup'} />
            <input type="text" name="pais" placeholder={deliveryType === 'pickup' ? 'País (opcional)' : 'País *'} value={formData.pais} onChange={handleChange} maxLength={50} required={deliveryType !== 'pickup'} />
            <input type="text" name="medioDePago" placeholder="Medio de pago (ej: transferencia, efectivo, etc.)" value={formData.medioDePago} onChange={handleChange} maxLength={80} />
            <div className="cart-cuit-field">
              <input type="text" name="cuit" placeholder={requiresCuitByAfip(finalPrice) ? "CUIT/CUIL (obligatorio para esta compra - AFIP)" : "CUIT/CUIL (opcional - para recibir factura con tu número)"} value={formData.cuit} onChange={handleChange} maxLength={15} required={requiresCuitByAfip(finalPrice)} />
              {requiresCuitByAfip(finalPrice) && <span className="cart-cuit-hint">Obligatorio para compras de ${formatNumber(montoMaxSinCuit)} o más (AFIP)</span>}
            </div>
            <textarea maxLength={500} name="informacionAdicional" placeholder="Información adicional (opcional)" value={formData.informacionAdicional} onChange={handleChange} />
            <button 
              type="submit" 
              className="cart-button cart-button--submit" 
              disabled={isLoading || !formData.nombre.trim() || !formData.email.trim()}
            >
              {isLoading ? 'Enviando...' : 'Enviar pedido'}
            </button>
          </form>
        </div>
      )}

      {showCheckoutForm && (
        <div className="order-form">
          <h3 className="order-form-title">Información para comprar con MercadoPago</h3>
          {deliveryType === 'pickup' && (
            <p className="cart-pickup-notice">Retiro en local: no es necesario completar la dirección de envío.</p>
          )}
          <form>
            <input type="text" name="nombre" placeholder="Nombre completo y Apellido *" value={formData.nombre} onChange={handleChange} maxLength={50} required />
            <input type="tel" name="telefono" placeholder="Teléfono *" value={formData.telefono} onChange={handleChange} maxLength={20} required />
            <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleChange} maxLength={100} required />
            <input type="text" name="domicilio" placeholder={deliveryType === 'pickup' ? 'Domicilio (opcional - retiro en local)' : 'Domicilio *'} value={formData.domicilio} onChange={handleChange} maxLength={100} />
            <input type="text" name="localidad" placeholder={deliveryType === 'pickup' ? 'Localidad (opcional)' : 'Localidad *'} value={formData.localidad} onChange={handleChange} maxLength={50} />
            <input type="text" name="codigoPostal" placeholder={deliveryType === 'pickup' ? 'Código Postal (opcional)' : 'Código Postal *'} value={formData.codigoPostal} onChange={handleChange} maxLength={10} />
            <input type="text" name="provincia" placeholder={deliveryType === 'pickup' ? 'Provincia (opcional)' : 'Provincia *'} value={formData.provincia} onChange={handleChange} maxLength={50} />
            <input type="text" name="pais" placeholder={deliveryType === 'pickup' ? 'País (opcional)' : 'País *'} value={formData.pais} onChange={handleChange} maxLength={50} />
            <div className="cart-cuit-field">
              <input type="text" name="cuit" placeholder={requiresCuitByAfip(finalPrice) ? "CUIT/CUIL (obligatorio para esta compra - AFIP)" : "CUIT/CUIL (opcional - para recibir factura con tu número)"} value={formData.cuit} onChange={handleChange} maxLength={15} required={requiresCuitByAfip(finalPrice)} />
              {requiresCuitByAfip(finalPrice) && <span className="cart-cuit-hint">Obligatorio para compras de ${formatNumber(montoMaxSinCuit)} o más (AFIP)</span>}
            </div>
            <textarea maxLength={500} name="informacionAdicional" placeholder="Información adicional (opcional)" value={formData.informacionAdicional} onChange={handleChange} />
            <button 
              type="button" 
              className="view-toggle-btn" 
              onClick={handleCheckoutPro} 
              disabled={isLoading || !formData.nombre.trim() || !formData.email.trim() || !formData.telefono.trim() || (deliveryType === 'shipping' && !formData.domicilio.trim())}
            >
              {isLoading ? 'Procesando...' : (
                <>
                  <img src={mercadopagoLogo} alt="MercadoPago" className="mercadopago-logo" />
                  Continuar con MercadoPago
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {cart.length > 0 && (
        <div className="cart-discounts-block">
          <button type="button" className="discount-button" onClick={() => setShowDiscounts(!showDiscounts)}>
            {showDiscounts ? 'Ocultar descuentos disponibles' : 'Mostrar descuentos disponibles'}
          </button>

          {showDiscounts && (
            <div className="discount-list">
              <h3 className="discount-list-title">
                🎉 ¡Descuentos especiales disponibles!
              </h3>
              {discounts.map((discount, index) => (
                <div key={index} className="discount-item">
                  <div className="discount-item-header">
                    <span className="discount-icon">💰</span>
                    <span className="discount-percentage">-{discount.percentage}%</span>
                  </div>
                  <div className="discount-details">
                    {discount.minQuantity && discount.minQuantity !== 'N/A' && (
                      <span className="discount-detail">
                        📦 Cantidad mínima: {discount.minQuantity} unidades
                      </span>
                    )}
                    {discount.minAmount && discount.minAmount !== 'N/A' && (
                      <span className="discount-detail">
                        💵 Monto mínimo: ${formatNumber(discount.minAmount)}
                      </span>
                    )}
                    {discount.keyword && discount.keyword !== 'N/A' && (
                      <span className="discount-detail">
                        🎫 Con código de descuento
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Cart;